import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';
import Calendar from 'react-calendar';
import { Range, View } from 'react-calendar/dist/cjs/shared/types';

import { ErrorMessage, Typography } from '@/shared/components/atoms';
import { ICalendarDatePickerProps } from '@/shared/components/atoms/CalendarDatePicker/ICalendarDatePicker';
import InputLabel from '@/shared/components/atoms/InputLabel';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import { TippyTooltipRef } from '@/shared/components/atoms/TippyTooltip/ITippyTooltip';
import { getLocalizedDate } from '@/shared/lib/dates';

import CalendarIcon from '~/images/shared/calender.svg';

const CalendarDatePicker: React.FC<ICalendarDatePickerProps> = ({
  id,
  label,
  placeholder,
  value,
  disabled,
  required,
  error,
  highlightSelected = false,
  onChange,
  ...props
}) => {
  const {
    t,
    i18n: { language, dir },
  } = useTranslation();

  const isRange = props?.returnValue === 'range' || props?.selectRange;

  //***** hide using tooltip ref *****/
  const tooltipRef = useRef<TippyTooltipRef>(null);

  const hide = () => {
    if (tooltipRef) tooltipRef.current?.hide();
  };

  //***** Utils *****/
  const getInitialActiveStartDate = () => {
    if (value)
      return isRange ? (value as Range<Date | null>)[0] : (value as Date);
    return new Date();
  };

  const formatDate = (date: Date | null) =>
    getLocalizedDate({
      date,
      locale: language,
      formatDate: 'MMM d, yyyy',
    });

  //***** Control view and active start date *****/
  const [view, setView] = useState<View>('month');
  const [activeStartDate, setActiveStartDate] = useState<Date | null>(
    getInitialActiveStartDate()
  );

  // reset view and active start date on hide
  const onHidden = () => {
    setView('month');
    setActiveStartDate(getInitialActiveStartDate());
  };

  return (
    <TippyTooltip
      arrow={false}
      placement={dir() === 'ltr' ? 'top-start' : 'top-end'}
      padding='p-0'
      trigger='click'
      onHidden={onHidden}
      hideOnContentClick={false}
      disabled={disabled}
      ref={tooltipRef}
      content={
        <Calendar
          locale={language}
          onChange={(value, e) => {
            if (onChange) onChange(value, e);
            hide();
          }}
          value={value}
          view={view}
          onViewChange={({ view }) => setView(view)}
          activeStartDate={activeStartDate ?? undefined}
          onActiveStartDateChange={({ activeStartDate }) =>
            setActiveStartDate(activeStartDate)
          }
          {...props}
        />
      }
    >
      <>
        {label && (
          <InputLabel
            label={label}
            className='block'
            required={required}
            disabled={disabled}
            id={id}
          />
        )}
        <div
          id={id}
          className={classNames(
            'flex items-center gap-8 rounded-xl px-16 py-14',
            value && highlightSelected
              ? 'bg-surface-20'
              : 'border-xs border-outline-variant',
            disabled && 'pointer-events-none cursor-not-allowed opacity-5'
          )}
        >
          <span className='text-primary-40'>
            <CalendarIcon width='24' height='24' />
          </span>
          <Typography
            weight='medium'
            className={classNames(
              value && highlightSelected && 'text-primary-20'
            )}
          >
            {value
              ? isRange
                ? `${formatDate(
                    (value as Range<Date | null>)[0]
                  )} - ${formatDate((value as Range<Date | null>)[1])}`
                : formatDate(value as Date)
              : placeholder || t('date')}
          </Typography>
        </div>
        {error && <ErrorMessage errorMessage={error} />}
      </>
    </TippyTooltip>
  );
};

export default CalendarDatePicker;
